
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  TrendingUp, 
  DollarSign,
  Download,
  BarChart3
} from 'lucide-react';
import { calculateAmortizationSchedule, AmortizationPayment } from '../utils/amortizationCalculations';

interface LoanOption {
  id: string;
  programName: string;
  interestRate: number;
  monthlyPayment: number;
  totalPayment: number;
}

interface AmortizationModalProps {
  loan: LoanOption | null;
  loanAmount: number;
  isOpen: boolean;
  onClose: () => void;
}

export const AmortizationModal: React.FC<AmortizationModalProps> = ({
  loan,
  loanAmount,
  isOpen,
  onClose
}) => {
  const [viewType, setViewType] = useState<'monthly' | 'yearly'>('yearly');

  const amortizationData = useMemo(() => {
    if (!loan) return null;
    return calculateAmortizationSchedule(loanAmount, loan.interestRate, 30);
  }, [loan, loanAmount]);

  if (!loan || !amortizationData) return null;

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDecimal = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const currentPayments = viewType === 'monthly' 
    ? amortizationData.monthlyPayments 
    : amortizationData.yearlyPayments;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Amortization Schedule - {loan.programName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Total Interest Paid
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {formatCurrency(amortizationData.totalInterest)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Total of Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(loan.totalPayment)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Interest to Principal Ratio
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {((amortizationData.totalInterest / loanAmount) * 100).toFixed(1)}%
                </div>
              </CardContent>
            </Card>
          </div>

          {/* View Toggle */}
          <Tabs value={viewType} onValueChange={(value: any) => setViewType(value)}>
            <div className="flex justify-between items-center">
              <TabsList>
                <TabsTrigger value="yearly">Yearly View</TabsTrigger>
                <TabsTrigger value="monthly">Monthly View</TabsTrigger>
              </TabsList>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export Schedule
              </Button>
            </div>

            <TabsContent value={viewType} className="space-y-4">
              {/* Schedule Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    {viewType === 'monthly' ? 'Monthly' : 'Annual'} Payment Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3">{viewType === 'monthly' ? 'Month' : 'Year'}</th>
                          <th className="text-right p-3">Payment</th>
                          <th className="text-right p-3">Principal</th>
                          <th className="text-right p-3">Interest</th>
                          <th className="text-right p-3">Balance</th>
                          <th className="text-right p-3">Cumulative Interest</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentPayments.slice(0, viewType === 'monthly' ? 60 : 30).map((payment) => (
                          <tr key={payment.paymentNumber} className="border-b hover:bg-muted/50">
                            <td className="p-3">
                              <Badge variant="outline">
                                {viewType === 'monthly' ? `Month ${payment.paymentNumber}` : `Year ${payment.paymentNumber}`}
                              </Badge>
                            </td>
                            <td className="text-right p-3 font-medium">
                              {formatDecimal(payment.paymentAmount)}
                            </td>
                            <td className="text-right p-3 text-green-600">
                              {formatDecimal(payment.principalPayment)}
                            </td>
                            <td className="text-right p-3 text-orange-600">
                              {formatDecimal(payment.interestPayment)}
                            </td>
                            <td className="text-right p-3">
                              {formatCurrency(payment.remainingBalance)}
                            </td>
                            <td className="text-right p-3 text-muted-foreground">
                              {formatCurrency(payment.cumulativeInterest)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {currentPayments.length > (viewType === 'monthly' ? 60 : 30) && (
                    <div className="text-center p-4 text-muted-foreground">
                      Showing first {viewType === 'monthly' ? '60 months' : '30 years'}. 
                      Export full schedule to see all payments.
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Key Insights */}
          <Card>
            <CardHeader>
              <CardTitle>Key Insights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">First Payment Breakdown:</span>
                  <div className="ml-4 space-y-1">
                    <div>Principal: {formatDecimal(amortizationData.monthlyPayments[0].principalPayment)}</div>
                    <div>Interest: {formatDecimal(amortizationData.monthlyPayments[0].interestPayment)}</div>
                  </div>
                </div>
                <div>
                  <span className="font-medium">Final Payment Breakdown:</span>
                  <div className="ml-4 space-y-1">
                    <div>Principal: {formatDecimal(amortizationData.monthlyPayments[amortizationData.monthlyPayments.length - 1].principalPayment)}</div>
                    <div>Interest: {formatDecimal(amortizationData.monthlyPayments[amortizationData.monthlyPayments.length - 1].interestPayment)}</div>
                  </div>
                </div>
              </div>
              
              <div className="pt-2 border-t">
                <p className="text-muted-foreground">
                  Early in the loan, most of your payment goes toward interest. 
                  As time progresses, more goes toward principal, building equity in your home.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
